import { Component, Inject, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  AdvancedPaymentDto,
  BusinessType,
  ClientDto,
  CreatePaymentRequest,
  CurrencyDto,
  InvoiceDto,
  InvoiceType,
} from 'src/app/api/invoice/models';
import {
  AdvancePaymentsService,
  CurrencyService,
  InvoiceService,
  PaymentsService,
} from 'src/app/api/invoice/services';
import {
  IFilterInvoiceParams,
  IListParams,
  IModalOperation,
} from '../../models/user.model';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import moment from 'moment';
import { CONSTANTS } from '../../models/constants';
import { CurrencyConversionService } from '../../services/currencyConversion';

@Component({
  selector: 'app-pay-amount',
  templateUrl: './pay-amount.component.html',
  styleUrls: ['./pay-amount.component.scss'],
})
export class PayAmountComponent implements OnInit {
  paymentForm: UntypedFormGroup;
  loader = false;
  currencyOptions: CurrencyDto[] = [];
  invoiceOptions: InvoiceDto[] = [];
  currencyParams = { page: 0, size: 100, search: '' };
  showAlert = true;
  paymentCurrency = '';
  invoiceParams: IFilterInvoiceParams = {
    page: 0,
    size: 100,
    search: '',
    invoiceType: InvoiceType.Invoice,
    businessType: [],
    draft: false,
  };
  constants = CONSTANTS;

  paymentStatus = 'FULL';
  advancePaymentpaymentId = '';
  businessType: BusinessType[] = [];
  paymentDetails: AdvancedPaymentDto = {};
  private currencyInputSubject = new Subject<string>();
  private invoiceInputSubject = new Subject<string>();

  isFieldFocused = false;
  invoiceNumber = '';
  invoiceId = 0;
  processing = false;
  invoiceBalance = 0;
  selectedInvoice: InvoiceDto = {};

  public maxDate = new Date();

  constructor(
    private dialogRef: MatDialogRef<PayAmountComponent>,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private paymentService: PaymentsService,
    private advancePaymentService: AdvancePaymentsService,
    private currencyService: CurrencyService,
    private currencyConversionService: CurrencyConversionService,
    private invoiceService: InvoiceService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      isUpdate: boolean;
      amount: number;
      attachmentType: string;
      currency: string;
      invoiceId: InvoiceDto;
      paymentId: number;
      remarks: string;
    }
  ) {
    this.paymentForm = new UntypedFormGroup({
      invoiceId: new UntypedFormControl(null, [Validators.required]),
      paymentType: new UntypedFormControl({ name: 'Full', value: 'FULL' }, [
        Validators.required,
      ]),
      amountPaid: new UntypedFormControl(null, []),
      currencyId: new UntypedFormControl({ value: null, disabled: true }),
      invoiceBalance: new UntypedFormControl({ value: null, disabled: true }),
      balanceAmount: new UntypedFormControl({ value: '0', disabled: true }),
      remarks: new UntypedFormControl(data?.remarks ?? null, []),
    });
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const advancePaymentpaymentId = params['advancePaymentId'];
      this.advancePaymentpaymentId = advancePaymentpaymentId;
    });
    this.advancePaymentpaymentId && this.loadPaymentDetails();

    this.paymentForm
      .get('invoiceId')
      ?.valueChanges.subscribe((selectedValue: InvoiceDto) => {
        this.invoiceId = selectedValue?.id || 0;
        this.loadInvoiceBalance(selectedValue);
      });

    this.fetchCurrency(this.currencyParams);
    this.currencyInputSubject
      //  debounce time as needed
      .pipe(debounceTime(1000))
      .subscribe(searchTerm => {
        this.currencyParams.search = searchTerm;
        this.fetchCurrency(this.currencyParams);
      });

    this.invoiceInputSubject
      //  debounce time as needed
      .pipe(debounceTime(1000))
      .subscribe(searchTerm => {
        this.invoiceParams.search = searchTerm;
        this.fetchInvoice(this.invoiceParams);
      });

    this.paymentForm.get('paymentType')?.valueChanges.subscribe(data => {
      const currencyId = this.paymentForm.get('currencyId');
      const amountPaid = this.paymentForm.get('amountPaid');

      if (data.value === 'PARTIAL') {
        currencyId?.setValidators([Validators.required]);
        amountPaid?.setValidators([
          Validators.required,
          Validators.pattern(/^[0-9]+(\.[0-9]+)?$/),
        ]);
      } else {
        currencyId?.clearValidators();
        amountPaid?.clearValidators();
      }
      currencyId?.updateValueAndValidity();
      amountPaid?.updateValueAndValidity();
    });

    this.paymentForm
      .get('amountPaid')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe(data => {
        const amountPaid = this.paymentForm.get('amountPaid');
        const currencyId = this.paymentForm.get('currencyId');

        const paymentType = this.paymentForm.get('paymentType')?.value;
        if (paymentType.value === 'PARTIAL') {
          currencyId?.setValidators([Validators.required]);
          const maxBalance = Math.min(
            Number(
              this.calculatePaymentBalance(
                this.paymentDetails.balance || 0,
                this.data.amount
              )
            ),
            Number(
              this.updateTotalBalance(
                this.paymentCurrency,
                this.selectedInvoice
              )
            )
          );

          amountPaid?.setValidators([
            Validators.required,
            Validators.pattern(/^[0-9]+(\.[0-9]+)?$/),
            Validators.max(Number(maxBalance)),
          ]);
        } else if (paymentType.value === 'FULL') {
          amountPaid?.clearValidators();
          currencyId?.clearValidators();
        }
        amountPaid?.updateValueAndValidity();
        currencyId?.updateValueAndValidity();
      });

    if (this.data.isUpdate) {
      this.invoiceId && this.data.invoiceId.id;
      this.invoiceId && this.loadInvoiceBalance(this.data?.invoiceId);
      this.paymentCurrency = this.data.currency;
      this.paymentForm?.patchValue({
        amountPaid: this.data?.amount,
        currencyId: this.data?.currency,
        paymentType: this.data?.attachmentType,
        invoiceId: this.data?.invoiceId,
      });
      this.paymentForm.get('paymentType')?.disable();
      this.paymentForm.get('invoiceId')?.disable();
    }
  }

  loadInvoiceBalance(selectedValue: InvoiceDto) {
    const params = { id: this.invoiceId };
    if (this.invoiceId) {
      this.paymentService.getBalanceByInvoiceId(params).subscribe({
        next: response => {
          if (response?.data) {
            this.invoiceBalance = response?.data?.totalBalance || 0;

            this.paymentForm?.patchValue({
              invoiceBalance: this.updateTotalBalance(
                this.paymentCurrency,
                selectedValue
              ),
            });

            this.paymentStatus =
              (this.updateTotalBalance(this.paymentCurrency, selectedValue) ||
                0) >
                this.calculatePaymentBalance(
                  this.paymentDetails.balance || 0
                ) || selectedValue?.PaymentState === 'PARTIALLYPAID'
                ? 'PARTIAL'
                : 'FULL';

            if (this.paymentStatus === 'PARTIAL') {
              this.paymentForm?.patchValue({
                paymentType: { name: 'Partial', value: 'PARTIAL' },
              });
            }
          }
        },
        error: error => {
          this.toastr.error(error?.error?.message);
        },
      });
    }
  }

  paymentTypeOptions = [
    { name: 'Full', value: 'FULL' },
    { name: 'Partial', value: 'PARTIAL' },
  ];

  partialPaymentOptions = [{ name: 'Partial', value: 'PARTIAL' }];

  closeAlert() {
    this.showAlert = false;
  }
  // invoice balance calculation
  updateTotalBalance(
    paymentCurrency: string,
    selectedValue: InvoiceDto
  ): number | null {
    const totalBalance = this.invoiceBalance || 0;
    const currenciesRequiringConversion = ['SAR', 'USD'];
    this.selectedInvoice = selectedValue;
    const amountPaid = this.data.amount || 0;

    // if invoice currency and payment currency same
    if (
      paymentCurrency === selectedValue?.quotation?.currency?.code ||
      paymentCurrency === selectedValue?.serviceOrder?.currency?.code ||
      paymentCurrency === selectedValue?.workOrders?.[0]?.currency?.code
    ) {
      return totalBalance + amountPaid;
    }

    if (
      (paymentCurrency === 'USD' &&
        currenciesRequiringConversion.includes(
          selectedValue?.quotation?.currency?.code || ''
        )) ||
      (paymentCurrency === 'USD' &&
        currenciesRequiringConversion.includes(
          selectedValue?.serviceOrder?.currency?.code || ''
        )) ||
      (paymentCurrency === 'USD' &&
        currenciesRequiringConversion.includes(
          selectedValue?.workOrders?.[0]?.currency?.code || ''
        ))
    ) {
      return (
        this.currencyConversionService.convertToUSD(totalBalance) + amountPaid
      );
    }

    if (
      (paymentCurrency === 'SAR' &&
        currenciesRequiringConversion.includes(
          selectedValue?.quotation?.currency?.code || ''
        )) ||
      (paymentCurrency === 'SAR' &&
        currenciesRequiringConversion.includes(
          selectedValue?.serviceOrder?.currency?.code || ''
        )) ||
      (paymentCurrency === 'SAR' &&
        currenciesRequiringConversion.includes(
          selectedValue?.workOrders?.[0]?.currency?.code || ''
        ))
    ) {
      return (
        this.currencyConversionService.convertToSAR(totalBalance) + amountPaid
      );
    }

    return null;
  }

  fetchCurrency(params: IListParams) {
    this.currencyService.getAllCurrencies(params).subscribe({
      next: response => {
        if (response?.data) {
          this.currencyOptions = response.data[0]?.data || [];
        }
      },
      error: error => {
        this.toastr.error(error?.error?.message);
      },
    });
  }

  loadPaymentDetails() {
    const params = { id: parseInt(this.advancePaymentpaymentId) };
    this.advancePaymentService.getPaymentById(params).subscribe({
      next: response => {
        if (response?.data?.[0]) {
          this.paymentDetails = response.data[0];
          this.businessType = this.paymentDetails?.businessTypes || [];
          this.invoiceParams.businessType = this.businessType;
          this.invoiceParams.clientId = this.paymentDetails?.client?.id;

          this.paymentCurrency = this.paymentDetails?.currency?.code || '';
          this.fetchInvoice(this.invoiceParams);
          this.paymentForm?.patchValue({
            balanceAmount: this.calculatePaymentBalance(
              this.paymentDetails.balance || 0,
              this.data.amount
            ),
            currencyId: this.paymentDetails?.currency,
          });
        }
      },
      error: error => {
        this.toastr.error(error?.error?.message);
      },
    });
  }

  // calculate payment balance
  calculatePaymentBalance(paymentBalance: number, amountAlreadyPaid = 0) {
    let total = 0;
    total = amountAlreadyPaid + paymentBalance;
    return total;
  }

  fetchInvoice(params: IFilterInvoiceParams) {
    this.invoiceService.getAllInvoices(params).subscribe({
      next: response => {
        if (response?.data) {
          this.invoiceOptions = response.data[0]?.data || [];
        }
      },
      error: error => {
        this.toastr.error(error?.error?.message);
      },
    });
  }

  customSearchFn(term: string, item: ClientDto) {
    return item;
  }
  customSearchFnCurrency(term: string, item: CurrencyDto) {
    return item;
  }
  onFocus(isFocused: boolean, name: string) {
    this.isFieldFocused = isFocused;

    switch (name) {
      case 'currencyId':
        if (!isFocused) {
          this.currencyInputSubject.next('');
        }
        break;

      default:
        break;
    }
  }
  onInputCurrency(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.currencyInputSubject.next(searchTerm);
  }
  onInputInvoice(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.invoiceInputSubject.next(searchTerm);
  }
  clearSearchCurrency() {
    this.currencyParams.search = '';
    this.fetchCurrency(this.currencyParams);
  }
  clearSearchInvoice() {
    this.invoiceParams.search = '';
    this.fetchInvoice(this.invoiceParams);
  }

  onSubmit() {
    this.loader = true;
    this.processing = true;
    if (this.paymentForm.valid) {
      const { amountPaid } = this.paymentForm.value;
      const { paymentType, invoiceId } = this.paymentForm.value;
      const formData: CreatePaymentRequest = {
        ...this.paymentForm.value,
        advancePaymentId: this.advancePaymentpaymentId,
        attachmentType: paymentType?.value,
        attachmentDate: moment().format('YYYY-MM-DD'),
        invoiceId: invoiceId?.id,
        amountPaid: amountPaid,
        remarks: this.paymentForm?.value?.remarks,

        // paymentType?.value === 'PARTIAL'
        //   ? amountPaid
        //   : this.updateTotalBalance(
        //       this.paymentCurrency,
        //       this.selectedInvoice
        //     ) || 0,
      };

      // If amountPaid is not defined or null, set it to invoiceBalance
      if (formData.amountPaid === undefined || formData.amountPaid === null) {
        formData.amountPaid =
          this.updateTotalBalance(this.paymentCurrency, this.selectedInvoice) ||
          0;
      }

      if (this.data.isUpdate) {
        this.paymentService
          .updatePaymentAttachmentById({
            body: formData,
            id: Number(this.data.paymentId),
          })
          .subscribe({
            next: response => {
              this.dialogRef.close();
              this.closeDialogWithData({
                operation: CONSTANTS.CREATE_OPERATION,
              });
              this.loader = false;
              this.toastr.success(response?.message);
            },
            error: errorResp => {
              this.loader = false;
              this.toastr.error(errorResp.error?.message);
            },
          });
      } else {
        this.paymentService.createPayment({ body: formData }).subscribe({
          next: response => {
            this.dialogRef.close();
            this.closeDialogWithData({ operation: CONSTANTS.CREATE_OPERATION });
            this.loader = false;

            this.toastr.success(response?.message);
          },
          error: errorResp => {
            this.loader = false;

            this.toastr.error(errorResp.error?.message);
          },
        });
      }
    }
  }

  closeDialogWithData(data: IModalOperation): void {
    this.dialogRef.close(data);
  }

  onCancel() {
    this.dialogRef.close();
    this.closeDialogWithData({ operation: CONSTANTS.CANCEL_OPERATION });
  }
}
